/**
 * Converts an image file to a base64 string
 * @param file - The image file to convert
 * @returns Promise that resolves with the base64 string
 */
export const imageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert image to base64'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };
    reader.readAsDataURL(file);
  });
};


/**
 * Converts a File object (e.g., a video file) into a Base64 encoded string.
 * This function is designed for use in a browser environment.
 *
 * @param file The File object to convert (e.g., obtained from an <input type="file"> element).
 * @returns A Promise that resolves with the Base64 encoded string of the file,
 * or rejects with an error if the conversion fails.
 */
export function videoToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    // Check if the provided input is a File object
    if (!(file instanceof File)) {
      reject(new Error("Input must be a File object."));
      return;
    }

    // Create a new FileReader instance
    const reader = new FileReader();

    // Define the onload event handler: this is called when the read operation is successfully completed.
    reader.onload = () => {
      // The result property contains the data as a URL representing the file's data as a base64 encoded string.
      // We assert it as a string because `reader.result` can be `string | ArrayBuffer | null`.
      resolve(reader.result as string);
    };

    // Define the onerror event handler: this is called if an error occurs during the read operation.
    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error.target?.error?.message || 'Unknown error'}`));
    };

    // Define the onabort event handler: this is called if the read operation is aborted.
    reader.onabort = () => {
      reject(new Error("File read operation was aborted."));
    };

    // Start reading the file's contents as a Data URL (Base64 encoded string).
    // This will trigger the onload, onerror, or onabort events.
    reader.readAsDataURL(file);
  });
}